import React, { useEffect, useCallback, useState } from 'react';
import { Switch as FormCardSwitch } from '@material-ui/core';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';

import FormCard from '../../../../components/Cards/FormCard';
import { HeadingThree } from '../../../../components/Headings';
import colors from '../../../../styles/Colors';
import { isEmpty } from 'Helpers/isEmpty';
import { Product } from 'Constants/productType';
import { SimpleMenu } from '../../../../components/SimpleMenu';

const ProductCardBase = props => {
  const {
    className,
    reservationEdit,
    event,
    isOpen,
    isReview,
    order = {},
    productType,
    setIsOpen,
    hasInitialProduct,
    setIsWarningOpen,
    simpleDeleteDisabled
  } = props;

  const eventSelected = event && !isEmpty(event);
  const hasRequiredAddOns =
    event?.venue?.venueConfig?.requiredAddons &&
    event?.requiredAddOns?.length > 0 &&
    productType === 'addOnProduct' &&
    event?.addOnProducts?.filter(x => !x.disabled)?.length > 0;
  const [isProductAvailableForSelectedEvent, setIsAvailableForSelectedEvent] = useState(false);

  const { orderItems = [] } = order || {};
  const reservationOrderItemArray =
    orderItems &&
    orderItems.filter(orderItem => {
      return productType === Product.ADD_ON
        ? orderItem.addOnProduct
        : orderItem.reservation &&
            // eslint-disable-next-line no-prototype-builtins
            orderItem.reservation.hasOwnProperty(productType);
    });
  const reservationOrderItem = reservationOrderItemArray ? reservationOrderItemArray[0] : {};
  const { reservation } = reservationOrderItem ? reservationOrderItem : {};

  // Gets display text from productType
  const getProductName = useCallback(productType => {
    switch (productType) {
      case Product.STALL:
        return 'Stall';
      case Product.RV:
        return 'RV Spot';
      case Product.ADD_ON:
        return 'Add On';
      case 'NOTES':
        return ' Admin Note';
      default:
        return '';
    }
  });

  // Gets the property on the getQuery response data from productType
  const getProductPropOnData = useCallback(productType => {
    switch (productType) {
      case Product.STALL:
        return 'stallProducts';
      case Product.RV:
        return 'rvProducts';
      case Product.ADD_ON:
        return 'addOnProducts';
      default:
        return 'stallProducts';
    }
  });

  useEffect(() => {
    if (setIsOpen && !event?.venue?.venueConfig?.requiredAddons) {
      setIsOpen(false);
    }

    if (setIsOpen && hasRequiredAddOns) {
      setIsOpen(true);
    }
  }, [event.id]);

  useEffect(() => {
    if (productType === 'NOTES') {
      setIsAvailableForSelectedEvent(true);
    } else {
      const product = getProductPropOnData(productType);
      if (event && event[product]) {
        setIsAvailableForSelectedEvent(event[product].length > 0);
      } else {
        setIsAvailableForSelectedEvent(false);
      }
    }
  }, [event]);

  const getReservationSection = () => {
    const options = [
      { label: 'Edit', onClick: () => (setIsOpen ? setIsOpen(!isOpen) : {}), dataTestId: 'simple-menu-edit', disabled: !hasInitialProduct },
      {
        label: 'Delete',
        disabled: simpleDeleteDisabled,
        dataTestId: 'simple-menu-delete',
        onClick: () => (setIsWarningOpen ? setIsWarningOpen() : {})
      }
    ];
    return (
      <div className="edit-options-container">
        {!reservationEdit ? (
          <button type="button" onClick={setIsOpen ? () => setIsOpen(!isOpen) : undefined}>
            {isOpen ? 'Close' : 'Edit'}
          </button>
        ) : (
          <SimpleMenu isParentOpen={isOpen} options={options} handleParentClose={() => (setIsOpen ? setIsOpen(false) : {})} />
        )}
      </div>
    );
  };

  const getIncludeSection = productType => {
    return (
      <div className="switch-container" data-testid={`collapsed-section-${productType}`}>
        <p>Include {getProductName(productType)}s?</p>
        <FormCardSwitch
          color={'primary'}
          disabled={!eventSelected && !reservationEdit}
          onChange={setIsOpen ? () => setIsOpen(!isOpen) : undefined}
          size={'medium'}
          checked={isOpen}
        />
      </div>
    );
  };

  const getNoSelectionSection = productType => {
    return (
      <div className="switch-container" data-testid={`no-selection-${productType}`}>
        <p>Select an event to enter {getProductName(productType)} details</p>
      </div>
    );
  };

  return (
    <ProductCard isMobile={isMobile} className={`product-card ${className} ${isMobile ? 'mobile' : ''}`} dataTestId={`card_${productType}`}>
      <div
        data-testid={`product-card-${productType}`}
        className={`title-row ${!isProductAvailableForSelectedEvent && (eventSelected || reservationEdit) ? 'disabled' : ''}`}>
        <HeadingThree label={`${getProductName(productType)}s`} className="" />
        {!eventSelected && !reservationEdit && getNoSelectionSection(productType)}
        {reservationEdit && !order.canceled && isProductAvailableForSelectedEvent && getReservationSection()}
        {isProductAvailableForSelectedEvent && !reservation && !isReview && !reservationEdit && !hasRequiredAddOns && getIncludeSection(productType)}
      </div>
      {isProductAvailableForSelectedEvent && props.children}
    </ProductCard>
  );
};

const ProductCard = styled(FormCard)`
  padding: 20px;
  min-width: 400px;
  overflow: visible;

  ${({ isMobile }) =>
    isMobile &&
    `
      min-width: auto;
      width: 100%;
    `}

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.disabled {
      color: #6b6e74;
      height: 57px;
    }

    h3 {
      text-transform: capitalize;
    }
  }

  .switch-container {
    display: flex;
    justify-content: center;
    align-items: center;

    p {
      margin: 0;
    }
  }

  .edit-options-container,
  .switch-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .MuiSwitch-colorPrimary.Mui-checked {
    color: ${colors.white};
  }

  .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track {
    background-color: ${colors.button.primary.active};
    opacity: 1;
  }

  .edit-options-container button {
    padding: 0 10px;
    background: none;
    border: none;
    font-family: 'IBMPlexSans-Regular';
    font-size: 15px;
    letter-spacing: 1.05px;
    line-height: 17px;
    text-transform: uppercase;
  }

  .edit-options-container button:first-child {
    color: ${colors.error.primary};
  }

  .edit-options-container button:last-child {
    color: ${colors.border.tertiary};
  }

  .edit-options-container button:focus {
    outline: none;
  }

  .edit-options-container button:hover {
    cursor: pointer;
  }
`;

export default ProductCardBase;
